import {
    createColumnHelper,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import Fuse from 'fuse.js';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ArticleModel } from 'api/models';
import { CatalogueArticleTableBody } from 'app/modules/wallforming/components/catalogue-container/CatalogueArticleTableBody';
import { CatalogueArticleTableHeader } from 'app/modules/wallforming/components/catalogue-container/CatalogueArticleTableHeader';
import { getFilteredData } from 'app/modules/wallforming/components/catalogue-container/utils/articleFilterFunctions';
import { SearchInput } from 'app/modules/wallforming/components/search-field/SearchInput';
import { SettingTypeFilter } from 'app/modules/wallforming/components/setting-container/SettingTypeFilter';

import 'app/modules/wallforming/custom-styles/customCatalogueStyle.css';

const columnHelper = createColumnHelper<ArticleModel>();

interface CatalogueArticleTableProps {
    articleData: ArticleModel[] | undefined;
    showFilter?: boolean;
}

export const CatalogueArticleTable = ({ articleData, showFilter = true }: CatalogueArticleTableProps) => {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState<string>('');
    const [sorting, setSorting] = useState<SortingState>([]);

    const fuse = useMemo(() => {
        return articleData
            ? new Fuse(articleData, { keys: ['articleNumber', 'displayName'], threshold: 0.3, ignoreLocation: true })
            : null;
    }, [articleData]);

    const filteredData = useMemo(() => {
        return articleData ? getFilteredData(articleData, searchValue, fuse) : [];
    }, [articleData, searchValue, fuse]);

    const columns = useMemo(
        () => [
            columnHelper.accessor('articleNumber', {
                header: () => (
                    <div className="flex align-middle h-full justify-start text-[12.5px] pl-1 whitespace-nowrap overflow-visible text-ellipsis">
                        {t('labelArticleNumber')}
                    </div>
                ),
                cell: (info) => (
                    <div className="max-w-24 min-w-24 flex align-middle h-full justify-start pl-1">
                        {info.getValue()}
                    </div>
                )
            }),

            columnHelper.accessor('lifeCycleCode', {
                header: () => (
                    <div className="flex align-middle h-full justify-start text-[12.5px] pl-1.5 whitespace-nowrap overflow-visible text-ellipsis">
                        {'LC'}
                    </div>
                ),
                cell: (info) => (
                    <div className="max-w-20 min-w-20 flex align-middle h-full justify-start pl-1">
                        {info.getValue()}
                    </div>
                )
            }),

            columnHelper.accessor('displayName', {
                header: () => (
                    <div className="flex align-middle justify-start pl-1 text-[12.5px]">{t('labelArticleName')}</div>
                ),
                cell: (info) => (
                    <div className="max-w-24 min-w-24 flex align-middle justify-start pl-1">{info.getValue()}</div>
                )
            })
        ],
        [t]
    );

    // eslint-disable-next-line react-hooks/incompatible-library
    const articleTable = useReactTable({
        data: filteredData,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel()
    });

    const handleOnPointerLeave = useCallback((): void => {
        document.body.style.cursor = 'auto';
    }, []);

    return (
        <div
            className="flex flex-col h-full w-full justify-items-stretch align-middle p-3 sm:p-0 overflow-auto"
            onPointerLeave={handleOnPointerLeave}
        >
            <div className="flex w-full">
                <div className="flex justify-items-end justify-end align-middle w-full">
                    <div className="w-2/3 h-auto min-h-7">
                        <SearchInput
                            placeholder={t('labelSearchPlaceholder')}
                            value={searchValue}
                            onChange={setSearchValue}
                        />
                    </div>
                    {showFilter && <SettingTypeFilter />}
                </div>
            </div>

            <div className="flex-1 overflow-auto h-full w-full justify-items-stretch mt-4 pr-0 mr-0">
                <table className="border-collapse text-sm pr-0 table-auto w-full">
                    <CatalogueArticleTableHeader articleTableHeader={articleTable.getHeaderGroups()} />
                    <CatalogueArticleTableBody articleTableRows={articleTable.getRowModel().rows} columns={columns} />
                </table>
            </div>
        </div>
    );
};
